import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { TextField, IconButton, InputAdornment, lighten, Box } from '@mui/material';
import React, { forwardRef, useRef, useState, useEffect, ChangeEvent, FormEvent, MutableRefObject, Ref, RefCallback } from 'react';

import { TInput } from './Input.types';

import './Input.module.scss';

const combineRefs = <T extends HTMLElement>(...refs: Array<Ref<T> | undefined>) => {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        (ref as RefCallback<T>)(node);
      } else if (ref) {
        (ref as MutableRefObject<T | null>).current = node;
      }
    });
  };
};

const Input = forwardRef<HTMLInputElement, TInput>(
  ({ errorText, type, readonly, InputProps, inputProps, label, customAdornments = [], ...props }, ref) => {
    const localRef = useRef<HTMLInputElement>(null);
    const combinedRef = combineRefs(localRef, ref);

    const [inputType, setInputType] = useState(type);
    const [internalErrorText, setInternalErrorText] = useState(errorText);

    // Sync internal error state with the latest `errorText` prop.
    useEffect(() => {
      setInternalErrorText(errorText);
    }, [errorText]);

    // Prevent number input from changing value on mouse wheel scroll.
    useEffect(() => {
      if (inputType === 'number') {
        const el = localRef.current;
        const wheelHandler = (e: WheelEvent) => e.preventDefault();
        el?.addEventListener('wheel', wheelHandler, { passive: false });
        return () => el?.removeEventListener('wheel', wheelHandler);
      }
    }, [inputType]);

    // Listen for global 'form:clearValidation' event and clear error state
    // only if this input belongs to the targeted form (by formId).
    useEffect(() => {
      const inputEl = localRef.current;
      if (!inputEl) return;

      const handleClear = (e: Event) => {
        const event = e as CustomEvent<string>;
        const targetFormId = event.detail;

        const parentForm = inputEl.closest('form');
        const myFormId = parentForm?.dataset.formId;

        if (myFormId && myFormId === targetFormId) {
          setInternalErrorText(undefined);
        }
      };

      document.addEventListener('form:clearValidation', handleClear);
      return () => {
        document.removeEventListener('form:clearValidation', handleClear);
      };
    }, []);

    const passwordToggle =
      type === 'password' ? (
        <IconButton onClick={() => setInputType(inputType === 'password' ? 'text' : 'password')}>
          {inputType === 'password' ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      ) : null;

    return (
      <TextField
        {...props}
        className='react-components-input'
        inputRef={combinedRef}
        type={inputType}
        error={!!internalErrorText}
        helperText={internalErrorText}
        label={inputProps && inputProps.required ? `${label}*` : label}
        FormHelperTextProps={{
          sx: (theme) => ({
            position: 'absolute',
            bottom: 0,
            left: 0,
            transform: 'translate(0, calc(100% + 9px))',
            transformOrigin: 'top left',
            fontSize: '0.75rem',
            lineHeight: 1.66,
            pointerEvents: 'none',
            whiteSpace: 'pre-wrap',
            color: `${theme.palette.error.dark} !important`,
            backgroundColor: lighten(theme.palette.error.light, 0.9),
            border: `1px solid ${theme.palette.error.main}`,
            zIndex: 10,
            boxShadow: theme.shadows[3],
            padding: '2px 6px',
            borderRadius: '4px',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -6,
              left: 16,
              width: 10,
              height: 10,
              backgroundColor: lighten(theme.palette.error.light, 0.9),
              borderLeft: `1px solid ${theme.palette.error.main}`,
              borderTop: `1px solid ${theme.palette.error.main}`,
              transform: 'rotate(45deg)',
              zIndex: 11,
            },
          }),
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setInternalErrorText(e.target.validationMessage || errorText);
          props.onChange?.(e);
        }}
        onInvalid={(e: FormEvent<HTMLInputElement>) => {
          e.preventDefault();
          setInternalErrorText((e.target as HTMLInputElement).validationMessage);
        }}
        inputProps={{
          ...inputProps,
          readOnly: readonly,
        }}
        InputProps={{
          ...InputProps,
          endAdornment:
            customAdornments.length || passwordToggle ? (
              <InputAdornment position='end'>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {customAdornments}
                  {passwordToggle}
                </Box>
              </InputAdornment>
            ) : null,
        }}
      />
    );
  },
);

export default Input;
